import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import FileUploader from "@/components/Fields/FileUploader";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { inspectionResultUploadSchema } from "@/schemas/inspectionResultUploadSchema";
import { postUploadInspectionResult } from "@/services/inspectionResultService";
import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, useDisclosure, useToast } from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { Field, FieldProps, Form, Formik } from "formik";
import { useState } from "react";

import '../styles/Form.css';

type FormContents = {
    reportName: string,
    vesselName: string,
    imo: string,
    reportType: string,
    sireResult: File | null,
};

const initial: FormContents = {
    reportName: '',
    vesselName: '',
    imo: '',
    reportType: '',
    sireResult: null,
}

const AddInspectionResult = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sireResult, setSireResult] = useState<File | null>(null);
    const [title, setTitle] = useState("");
    const [dataEdit, setDataEdit] = useState<FormContents>(initial);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Upload?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = (data: FormContents) => {
        setTitle("Confirm Upload?");
        setConfirmationTest("Are you sure you want to upload this result?");
        setConfirmationTitleButton("Confirm Upload");
        onOpen();
        setDataEdit(data);
    }

    const generateDataUpload = () => {
        const data = new FormData();
        data.append('VesselName', dataEdit.vesselName);
        data.append('IMONumber', dataEdit.imo);
        data.append('ReportType', dataEdit.reportType);
        data.append('file', sireResult ?? '');
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel Upload?") {
            history.back();
            return;
        }

        setIsSubmitting(true)
        const obj = generateDataUpload();
        let response = {} as any;
        response = await postUploadInspectionResult(obj);

        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Upload Complete',
                description: "We've got your inspection result.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                history.back();
            }, 1000)
        } else {
            toast({
                title: 'Upload Failed',
                description: "We could not upload your inspection result.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDrop = (acceptedFiles: File[], setFieldValue: ((field: string, file: File) => void)) => {
        setFieldValue('sireResult', acceptedFiles[0]);
        setSireResult(acceptedFiles[0]);
    };

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={initial}
                    validationSchema={inspectionResultUploadSchema}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize
                >
                    {(props) => (
                        <Form>
                            {/*<Field name='reportName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.reportName && form.touched.reportName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Report Name</FormLabel>
                                            <Input {...field} placeholder='Report Name' className="form-input" isDisabled={isSubmitting} />
                                        </div>
                                        <FormErrorMessage>{String(form.errors.reportName)}</FormErrorMessage>
                                    </FormControl>
                                )}
                            </Field>*/}
                            <Field name='vesselName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.vesselName && form.touched.vesselName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Vessel Name</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Vessel Name' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.vesselName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='imo'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.imo && form.touched.imo)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">IMO Number</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='IMO Number' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.imo)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='reportType'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.reportType && form.touched.reportType)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Report Type</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Report type' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.reportType)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='sireResult'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.sireResult && (form.touched.sireResult || form.submitCount))} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">SIRE Result File</FormLabel>
                                            <div className="form-input-container">
                                                <FileUploader onDrop={(e) => handleDrop(e, props.setFieldValue)} isInvalid={Boolean(form.errors.sireResult && (form.touched.sireResult || form.submitCount))} isDisabled={props.isSubmitting} type='sire-report' />
                                                <FormErrorMessage>{String(form.errors.sireResult)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <p>All fields are required.</p>
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Upload
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default AddInspectionResult;
